// src/components/DashboardLayout.js
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, firestore, signOutIdentityIfActive } from '../firebase';
import { useAdminPermissions } from '../contexts/AdminPermissionsContext';
import AdminPermissionGuard from './AdminPermissionGuard';
import './DashboardLayout.css';
import { getEventEditionYear } from '../utils/eventRouting';
import { getActiveEventId } from '../utils/activeEvent';

/** Explicit classes — avoids global `.active` / other styles fighting the faint orange wash */
function sidebarNavLinkClass({ isActive }) {
  return `sidebar-nav-link${isActive ? ' sidebar-nav-link--active' : ''}`;
}

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessRoute } = useAdminPermissions();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);
  const [allFlyers, setAllFlyers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [activeEventYear, setActiveEventYear] = useState(null);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      await signOutIdentityIfActive();
      navigate('/admin');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function loadFlyers() {
      try {
        setSearchBusy(true);
        const snap = await getDocs(collection(firestore, 'flyers'));
        if (cancelled) return;
        const flyers = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (f) =>
              f &&
              (f.type === 'freshling' || f.type === 'mentor' || f.type === 'volunteer') &&
              f.eventId
          );
        setAllFlyers(flyers);
      } catch (err) {
        console.error('Global flyer search preload error:', err);
      } finally {
        if (!cancelled) setSearchBusy(false);
      }
    }
    loadFlyers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await getActiveEventId();
        if (cancelled) return;
        if (!id) {
          setActiveEventYear(null);
          return;
        }
        const snap = await getDoc(doc(firestore, 'events', id));
        if (cancelled) return;
        const ev = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setActiveEventYear(getEventEditionYear(ev));
      } catch {
        if (!cancelled) setActiveEventYear(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyNarrowSidebarDefault = useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (window.matchMedia('(max-width: 768px)').matches) {
      setSidebarCollapsed(true);
    }
  }, []);

  useEffect(() => {
    applyNarrowSidebarDefault();
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => applyNarrowSidebarDefault();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [applyNarrowSidebarDefault]);

  const updateSearchResults = (value) => {
    const term = (value || '').trim().toLowerCase();
    if (!term || !allFlyers.length) {
      setSearchResults([]);
      return;
    }
    const matches = allFlyers.filter((f) => {
      const email = (f.email || '').toString().toLowerCase();
      const name = (f.name || '').toString().toLowerCase();
      const properName = (f.properName || '').toString().toLowerCase();
      return (
        email.includes(term) ||
        name.includes(term) ||
        properName.includes(term)
      );
    }).slice(0, 25);
    setSearchResults(matches);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateSearchResults(value);
  };

  const navigateToFlyer = (flyer) => {
    if (!flyer || !flyer.eventId) return;
    const currentQuery = (searchTerm || '').trim();
    const primaryName =
      (flyer.type === 'freshling'
        ? (flyer.properName || flyer.name)
        : flyer.name) ||
      flyer.email ||
      '';
    if (primaryName) setSearchTerm(primaryName);
    setSearchResults([]);
    const flyerKey = (flyer.email || flyer.id || '').toString();
    const qParam = currentQuery ? `&q=${encodeURIComponent(currentQuery)}` : '';
    navigate(
      `/admin/flyer-management?eventId=${encodeURIComponent(
        flyer.eventId
      )}&flyer=${encodeURIComponent(flyerKey)}${qParam}`
    );
  };

  const handleGlobalFlyerSearch = (e) => {
    e.preventDefault();
    const q = (searchTerm || '').trim();
    if (!q) return;
    const term = q.toLowerCase();
    const matches = allFlyers
      .filter((f) => {
        const email = (f.email || '').toString().toLowerCase();
        const name = (f.name || '').toString().toLowerCase();
        const properName = (f.properName || '').toString().toLowerCase();
        return (
          email.includes(term) ||
          name.includes(term) ||
          properName.includes(term)
        );
      })
      .slice(0, 25);
    setSearchResults(matches);
    if (matches.length > 0) {
      const first = matches[0];
      navigate(
        `/admin/flyer-management?eventId=${encodeURIComponent(
          first.eventId
        )}&q=${encodeURIComponent(q)}`
      );
    } else {
      window.alert('No matching freshling or mentor found for that search.');
    }
  };

  return (
    <div className="dashboard-layout">
      <header className="dashboard-header">
        <div className="dashboard-header-bar">
          <div className="dashboard-header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed((c) => !c)}
              title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              {sidebarCollapsed ? (
                <span className="toggle-icon">&#9654;</span>
              ) : (
                <span className="toggle-icon">&#9664;</span>
              )}
            </button>
          </div>
          <div className="dashboard-header-center">
            <div className="header-title">
              <span className="header-title-wordmark">
                <span className="header-title-wordmark-fresh">Fresh </span>
                <span className="header-title-wordmark-meet">Meet</span>
                <span className="header-title-admin"> Admin</span>
              </span>
            </div>
          </div>
          <div className="dashboard-header-right">
            {activeEventYear && (
              <div className="header-quicklinks" role="group" aria-label="Quick links">
                {canAccessRoute('live') && (
                  <button
                    type="button"
                    className={`header-topnav-btn${location.pathname.startsWith(`/admin/live/${activeEventYear}`) ? ' header-topnav-btn--active' : ''}`}
                    onClick={() => navigate(`/admin/live/${activeEventYear}`)}
                    title={`Open live dashboard for ${activeEventYear}`}
                  >
                    Live ({activeEventYear})
                  </button>
                )}
                <button
                  type="button"
                  className="header-topnav-btn"
                  onClick={() => navigate(`/${activeEventYear}`)}
                  title={`Open public main page for ${activeEventYear}`}
                >
                  Main ({activeEventYear})
                </button>
              </div>
            )}
            <form className="header-search" onSubmit={handleGlobalFlyerSearch}>
              <input
                type="text"
                className="header-search-input"
                placeholder="Find flyer (name or email)…"
                value={searchTerm}
                onChange={handleSearchInputChange}
              />
              <button
                type="submit"
                className="header-topnav-btn header-topnav-btn--compact"
                disabled={searchBusy}
              >
                {searchBusy ? 'Searching…' : 'Search'}
              </button>
          {searchResults.length > 0 && (
            <div className="header-search-results">
              {searchResults.map((f) => {
                const primaryName =
                  (f.type === 'freshling'
                    ? (f.properName || f.name)
                    : f.name) ||
                  f.email ||
                  '(no name)';
                const secondary = `${f.email || ''}${f.type ? ` · ${f.type}` : ''}`;
                return (
                  <button
                    key={f.id}
                    type="button"
                    className="header-search-item"
                    onClick={() => navigateToFlyer(f)}
                  >
                    <span className="header-search-item-primary">{primaryName}</span>
                    {secondary && (
                      <span className="header-search-item-secondary">{secondary}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
            </form>
            <button type="button" className="header-topnav-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>
      <div className={`dashboard-body${!sidebarCollapsed ? ' dashboard-body--sidebar-open' : ''}`}>
        {!sidebarCollapsed && (
          <button
            type="button"
            className="sidebar-mobile-backdrop"
            aria-label="Close menu"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}
        <aside className={`sidebar ${sidebarCollapsed ? 'sidebar--collapsed' : ''}`}>
          <nav>
            <ul>
              <li className="sidebar-nav-group">
                <div className="sidebar-section-label">Overview</div>
                <ul className="sidebar-nav-group-list">
                  <li>
                    <NavLink to="/admin" end className={sidebarNavLinkClass}>
                      Dashboard
                    </NavLink>
                  </li>
                  {canAccessRoute('events') && (
                    <li>
                      <NavLink to="/admin/events" end className={sidebarNavLinkClass}>
                        Events
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>

              <li className="sidebar-nav-group">
                <div className="sidebar-section-label">Event setup</div>
                <ul className="sidebar-nav-group-list">
                  {canAccessRoute('printables') && (
                    <li>
                      <NavLink to="/admin/printables" end className={sidebarNavLinkClass}>
                        Printables
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('sponsors') && (
                    <li>
                      <NavLink to="/admin/sponsors" end className={sidebarNavLinkClass}>
                        Sponsors
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('tshirts') && (
                    <li>
                      <NavLink to="/admin/tshirts" end className={sidebarNavLinkClass}>
                        T-Shirts
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('fury-lab') && (
                    <li>
                      <NavLink to="/admin/fury-lab" end className={sidebarNavLinkClass}>
                        Fury Lab
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('recurrencies') && (
                    <li>
                      <NavLink to="/admin/recurrencies" end className={sidebarNavLinkClass}>
                        Recurrencies
                      </NavLink>
                    </li>
                  )}
                  {(canAccessRoute('freshmeet-hq') || canAccessRoute('main-clue-sponsor-run')) && (
                    <li>
                      <NavLink to="/admin/freshmeet-hq" end className={sidebarNavLinkClass}>
                        Main Clue
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('freshmeet-site') && (
                    <li>
                      <NavLink to="/admin/freshmeet-site" end className={sidebarNavLinkClass}>
                        Fresh Meet site
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('event-memory-book') && (
                    <li>
                      <NavLink to="/admin/event-memory-book" end className={sidebarNavLinkClass}>
                        Memory Book
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('photo-capture') && (
                    <li>
                      <NavLink to="/admin/photo-capture" end className={sidebarNavLinkClass}>
                        Photo capture
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('awards-ceremony') && (
                    <li>
                      <NavLink to="/admin/awards-ceremony" end className={sidebarNavLinkClass}>
                        Awards Ceremony
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>

              <li className="sidebar-nav-group">
                <div className="sidebar-section-label">People &amp; teams</div>
                <ul className="sidebar-nav-group-list">
                  {canAccessRoute('flyer-management') && (
                    <li>
                      <NavLink to="/admin/flyer-management" end className={sidebarNavLinkClass}>
                        Flyers
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('teams') && (
                    <li>
                      <NavLink to="/admin/teams" end className={sidebarNavLinkClass}>
                        Teams
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('team-pairing') && (
                    <li>
                      <NavLink to="/admin/team-pairing" end className={sidebarNavLinkClass}>
                        Team Pairing
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('load-order') && (
                    <li>
                      <NavLink to="/admin/load-order" end className={sidebarNavLinkClass}>
                        Load Order
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('event-manifest') && (
                    <li>
                      <NavLink to="/admin/event-manifest" end className={sidebarNavLinkClass}>
                        Day Manifest
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('tickets') && (
                    <li>
                      <NavLink to="/admin/tickets" end className={sidebarNavLinkClass}>
                        Tickets
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('volunteers') && (
                    <li>
                      <NavLink to="/admin/volunteers" end className={sidebarNavLinkClass}>
                        Volunteer shifts
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>

              <li className="sidebar-nav-group">
                <div className="sidebar-section-label">Scores &amp; puzzles</div>
                <ul className="sidebar-nav-group-list">
                  {canAccessRoute('bonus-puzzle-management') && (
                    <li>
                      <NavLink to="/admin/bonus-puzzle-management" end className={sidebarNavLinkClass}>
                        Bonus Puzzles
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('jump-it-stations') && (
                    <li>
                      <NavLink to="/admin/jump-it-stations" end className={sidebarNavLinkClass}>
                        Jump It — Stations
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('skyscore') && (
                    <li>
                      <NavLink to="/admin/skyscore" end className={sidebarNavLinkClass}>
                        Sky Scores
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('bonus-scores') && (
                    <li>
                      <NavLink to="/admin/bonus-scores" end className={sidebarNavLinkClass}>
                        Bonus Scores
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('score-import') && (
                    <li>
                      <NavLink to="/admin/score-import" end className={sidebarNavLinkClass}>
                        Import Scores
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('video-judge-queue') && (
                    <li>
                      <NavLink to="/admin/video-judge-queue" end className={sidebarNavLinkClass}>
                        Video Judge Queue
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>

              <li className="sidebar-nav-group">
                <div className="sidebar-section-label">Exports &amp; data</div>
                <ul className="sidebar-nav-group-list">
                  {canAccessRoute('team-export') && (
                    <li>
                      <NavLink to="/admin/team-export" end className={sidebarNavLinkClass}>
                        Export Teams
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('backups') && (
                    <li>
                      <NavLink to="/admin/backups" end className={sidebarNavLinkClass}>
                        Backups
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('test-data') && (
                    <li>
                      <NavLink to="/admin/test-data" end className={sidebarNavLinkClass}>
                        Test Data
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>

              <li className="sidebar-nav-group sidebar-nav-group--last">
                <div className="sidebar-section-label">System &amp; access</div>
                <ul className="sidebar-nav-group-list">
                  {canAccessRoute('users') && (
                    <li>
                      <NavLink to="/admin/users" end className={sidebarNavLinkClass}>
                        Admins
                      </NavLink>
                    </li>
                  )}
                  {canAccessRoute('templates') && (
                    <li>
                      <NavLink to="/admin/templates" end className={sidebarNavLinkClass}>
                        Templates
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          </nav>
        </aside>
        <main className="content">
          <AdminPermissionGuard>
            <Outlet />
          </AdminPermissionGuard>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
